import React from 'react';
import {
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  DirectionsBus as TransitIcon,
  Euro as EuroIcon,
  LocalGroceryStore as ShopsIcon,
  LocalHospital as HealthIcon,
  School as SchoolIcon,
  SquareFoot as SurfaceIcon,
  HomeWork as LotsIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import type { ProjectCardProject } from '../sections/ProjectCard';
import {
  buildProgramInfo,
  type PropertyProgramGroup,
} from '../../utils/propertyToProjectCard';
import {
  getProgramMapKeyFacts,
  getProgramServiceDistances,
} from '../../utils/mapProgramSummary';
import {
  projectStatusBadgeClass,
  projectStatusLabel,
  propertyProgramCategoryBadgeCompact,
} from '../../utils/programLabels';

interface MapSelectedProgramPanelProps {
  group: PropertyProgramGroup;
  project: ProjectCardProject;
  onViewSheet: () => void;
  onClose: () => void;
  /** Masquer « Voir fiche » (ex. déjà sur la page du programme). */
  showViewSheetButton?: boolean;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(price);

const MapSelectedProgramPanel: React.FC<MapSelectedProgramPanelProps> = ({
  group,
  project,
  onViewSheet,
  onClose,
  showViewSheetButton = true,
}) => {
  const program = buildProgramInfo(group);
  const facts = getProgramMapKeyFacts(group);
  const serviceDistances = getProgramServiceDistances(group.key);
  const statusLabel = project.statusLabel ?? projectStatusLabel(project.status);
  const statusClass = projectStatusBadgeClass(project.status);
  const categoryLabel =
    project.programTypeLabel ??
    propertyProgramCategoryBadgeCompact(group.representative.type);

  const serviceIcons: Record<string, React.ReactNode> = {
    shops: <ShopsIcon className="h-4 w-4" />,
    schools: <SchoolIcon className="h-4 w-4" />,
    transit: <TransitIcon className="h-4 w-4" />,
    health: <HealthIcon className="h-4 w-4" />,
  };

  return (
    <aside
      className="absolute bottom-4 right-3 z-20 w-[min(calc(100%-1.5rem),360px)] overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_16px_48px_rgba(0,0,0,0.18)] sm:right-4"
      aria-label={`Programme sélectionné : ${program.name}`}
    >
      {/* Infos clés — en-tête */}
      <div className="relative border-b border-neutral-100 bg-gradient-to-br from-neutral-50 to-white p-4 pr-10">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          aria-label="Fermer la sélection"
        >
          <CloseIcon className="h-4 w-4" />
        </button>

        <div className="flex gap-3">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg ring-1 ring-neutral-200">
            <img
              src={
                program.image ||
                'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=400&fit=crop'
              }
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0 flex-1">
            <span
              className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white ${statusClass}`}
            >
              {statusLabel}
            </span>
            <h3 className="font-display mt-1.5 line-clamp-2 text-sm font-bold leading-snug text-neutral-900">
              {project.title}
            </h3>
            <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              {program.location}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {facts.priceMin != null && facts.priceMin > 0 && (
            <span className="inline-flex items-center gap-1 rounded-lg bg-primary-50 px-2.5 py-1 text-xs font-bold tabular-nums text-primary-700">
              <EuroIcon className="h-3.5 w-3.5" />
              dès {formatPrice(facts.priceMin)}&nbsp;€
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-lg bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-700">
            <CalendarIcon className="h-3.5 w-3.5 text-neutral-500" />
            {project.dateLivraison ?? program.deliveryDate}
          </span>
          <span className="rounded-lg bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
            {categoryLabel}
          </span>
        </div>
      </div>

      {/* Container lots + infos + distances */}
      <div className="max-h-[min(42vh,320px)] space-y-3 overflow-y-auto bg-neutral-50/80 p-4">
        <div className="flex items-center justify-between gap-2 rounded-xl border border-neutral-200/80 bg-white px-3 py-2.5 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <LotsIcon className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs font-semibold text-neutral-900">
                {facts.lotsCount} lot{facts.lotsCount > 1 ? 's' : ''}
              </p>
              <p className="text-[11px] text-neutral-500">
                {facts.availableLotsCount} disponible
                {facts.availableLotsCount > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          {facts.typologiesLabel && (
            <span className="rounded-md bg-neutral-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
              {facts.typologiesLabel}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {facts.surfaceMin != null && (
            <div className="flex items-start gap-2 rounded-lg border border-neutral-200/80 bg-white px-2.5 py-2">
              <SurfaceIcon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                  Surfaces
                </p>
                <p className="text-xs font-semibold text-neutral-900">
                  {facts.surfaceMin === facts.surfaceMax
                    ? `${facts.surfaceMin} m²`
                    : `${facts.surfaceMin} – ${facts.surfaceMax} m²`}
                </p>
              </div>
            </div>
          )}
          {facts.energyClass && (
            <div className="flex items-start gap-2 rounded-lg border border-neutral-200/80 bg-white px-2.5 py-2">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-emerald-100 text-[10px] font-bold text-emerald-800">
                {facts.energyClass}
              </span>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                  DPE
                </p>
                <p className="text-xs font-semibold text-neutral-900">
                  Classe {facts.energyClass}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-white p-3 shadow-sm">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            Distances aux services
          </p>
          <ul className="grid grid-cols-2 gap-2">
            {serviceDistances.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-2 rounded-lg bg-neutral-50 px-2 py-1.5"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white text-primary-600 shadow-sm ring-1 ring-neutral-100">
                  {serviceIcons[item.id]}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-medium text-neutral-500">
                    {item.label}
                  </p>
                  <p className="text-xs font-bold tabular-nums text-neutral-900">
                    {item.distance}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] leading-snug text-neutral-400">
            Temps estimés à pied depuis le programme
          </p>
        </div>
      </div>

      {showViewSheetButton && (
        <div className="border-t border-neutral-100 bg-white p-3">
          <button
            type="button"
            onClick={onViewSheet}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-3 text-sm font-bold text-white shadow-md transition-colors hover:bg-primary-600"
          >
            Voir fiche
            <ArrowForwardIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </aside>
  );
};

export default MapSelectedProgramPanel;
